import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import axios from 'axios';

const SightingCropImage = ({ WebsitesDataID, BoundingBoxID, BoundingBox, onLoad }) => {
  const [cropSrc, setCropSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '2000px' } // Load images when they are 2000px from viewport
    );
    
    if (imgRef.current) {
      observer.observe(imgRef.current);
    }
    
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    
    let active = true;
    if (!WebsitesDataID || !BoundingBox) {
      setError(true);
      setLoading(false);
      return;
    }

    const loadCrop = async () => {
      setLoading(true);
      try {
        const websitesRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/screenshots/${WebsitesDataID}`,
          { headers: { 'x-api-key': process.env.REACT_APP_API_KEY } }
        );
        const imageLocation = websitesRes.data.ImageLocation;
        if (!imageLocation) throw new Error('No ImageLocation found');

        const cropRes = await axios.post(
          `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/game-management/upload-crop`,
          { image_location: imageLocation, bounding_box: BoundingBox },
          { headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY } }
        );

        if (active) {
          setCropSrc(cropRes.data.url);
          setLoading(false);
          if (onLoad) onLoad(cropRes.data.url, websitesRes.data);
        }
      } catch (err) {
        console.error('Failed to load crop:', err);
        if (active) {
          setError(true);
          setLoading(false);
        }
      }
    };

    loadCrop();
    return () => {
      active = false;
    };
  }, [WebsitesDataID, BoundingBoxID, BoundingBox, isVisible]);

  if (error) {
    return (
      <Box ref={imgRef} sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'action.disabledBackground', minHeight: 100 }}>
        <Typography variant="caption" color="text.secondary">No Image</Typography>
      </Box>
    );
  }

  if (loading || !isVisible) {
    return (
      <Box ref={imgRef} sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 100 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <img 
      ref={imgRef}
      src={cropSrc} 
      alt="Crop Sighting" 
      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
    />
  );
};

export default SightingCropImage;
